import { supabase } from '../supabase';

// Event categories
export const EventCategory = {
  Auth: 'auth',
  User: 'user',
  Post: 'post',
  Comment: 'comment',
  Group: 'group',
  Event: 'event',
  Product: 'product',
  Cart: 'cart',
  Order: 'order',
  Search: 'search',
  Navigation: 'navigation'
} as const;

// Event actions
export const EventAction = {
  View: 'view',
  Create: 'create',
  Update: 'update',
  Delete: 'delete',
  Like: 'like',
  Share: 'share',
  Comment: 'comment',
  Follow: 'follow',
  Join: 'join',
  Leave: 'leave',
  Search: 'search',
  AddToCart: 'add_to_cart',
  RemoveFromCart: 'remove_from_cart',
  Purchase: 'purchase',
  Cancel: 'cancel'
} as const;

interface AnalyticsEvent {
  category: typeof EventCategory[keyof typeof EventCategory];
  action: typeof EventAction[keyof typeof EventAction];
  label?: string;
  value?: number;
  metadata?: Record<string, any>;
}

class Analytics {
  private userId: string | null = null;
  private sessionId: string = crypto.randomUUID();
  private queue: AnalyticsEvent[] = [];
  private isProcessing: boolean = false;
  private batchSize: number = 10;
  private batchTimeout: number = 5000;
  private maxRetries: number = 3;
  private retryDelay: number = 1000;
  private timer: NodeJS.Timeout | null = null;
  private offlineQueue: AnalyticsEvent[] = [];
  private isOnline: boolean = navigator.onLine;

  constructor() {
    // Set up user ID when auth state changes
    supabase.auth.onAuthStateChange((event, session) => {
      this.userId = session?.user?.id || null;
      
      // Process offline queue when user logs in
      if (event === 'SIGNED_IN' && this.offlineQueue.length > 0) {
        this.queue.push(...this.offlineQueue);
        this.offlineQueue = [];
        this.processQueue().catch(console.error);
      }
    });

    // Handle online/offline status
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);

    // Process events on page unload
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  private handleOnline = () => {
    this.isOnline = true;
    if (this.offlineQueue.length > 0) {
      this.queue.push(...this.offlineQueue);
      this.offlineQueue = [];
      this.processQueue().catch(console.error);
    }
  };

  private handleOffline = () => {
    this.isOnline = false;
  };

  private handleBeforeUnload = () => {
    if (this.queue.length > 0) {
      try {
        localStorage.setItem('analytics_queue', JSON.stringify(this.queue));
      } catch (error) {
        console.warn('Failed to save analytics queue:', error);
      }
    }
  };

  private async processQueue(immediate: boolean = false) {
    if (this.isProcessing || (!immediate && this.queue.length < this.batchSize)) {
      return;
    }

    this.isProcessing = true;
    const events = this.queue.splice(0, this.batchSize);

    try {
      if (!this.isOnline) {
        this.offlineQueue.push(...events);
        return;
      }

      const { error } = await supabase
        .from('analytics_events')
        .insert(events.map(event => ({
          user_id: this.userId,
          session_id: this.sessionId,
          ...event,
          timestamp: new Date().toISOString()
        })));

      if (error) throw error;

    } catch (error) {
      console.warn('Error processing analytics events:', error);
      this.queue.unshift(...events);
    } finally {
      this.isProcessing = false;
      if (this.queue.length >= this.batchSize) {
        await this.processQueue();
      }
    }
  }

  public track(event: AnalyticsEvent) {
    if (!this.isOnline) {
      this.offlineQueue.push(event);
      return;
    }

    this.queue.push(event);

    if (this.timer) {
      clearTimeout(this.timer);
    }

    this.timer = setTimeout(() => {
      this.processQueue(true).catch(console.error);
    }, this.batchTimeout);

    if (this.queue.length >= this.batchSize) {
      this.processQueue().catch(console.error);
    }
  }

  public pageView(path: string = window.location.pathname) {
    this.track({
      category: EventCategory.Navigation,
      action: EventAction.View,
      label: path,
      metadata: {
        title: document.title,
        referrer: document.referrer
      }
    });
  }
}

// Create and export a single instance
const analytics = new Analytics();
export { analytics };